// import '@fontsource/inter';
import { CssVarsProvider } from '@mui/joy/styles';
import CssBaseline from '@mui/joy/CssBaseline';
import Home from './Home';
import { BrowserRouter, Routes, Route, Navigate } from "react-router";
import config from './config';
import * as Sentry from "@sentry/react";
import { CaptureConsole } from "@sentry/integrations";
import createUploadLink from "apollo-upload-client/createUploadLink.mjs";
import { ApolloClient, gql } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { ApolloProvider } from '@apollo/client/react';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { AUTH_TOKEN_NAME } from './constants';
import ErrorBoundary from './components/ErrorBoundary';
import { extendTheme } from '@mui/joy/styles';
import AuthLayout from './components/auth/AuthLayout';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { FeedbackProvider } from './components/feedback/Service';
import SignInForm from './components/auth/SignInForm';
import SignUpForm from './components/auth/SignUpForm';
import ConfirmEmail from './components/auth/ConfirmEmail';
import MainLayout from './components/MainLayout';
import Account from './components/Account';
import Company from './components/Company';
import Job from './components/Job';
import OnboardingJobEditor from './components/OnboardingJobEditor';
import Jobs from './components/Jobs';
import customFetch from './customFetch';
import Talent from './components/Talent';
import Questionnaire from './components/Questionnaire';
import Applications from './components/Applications';
import SalesLanding from './components/SalesLanding';
import JobCandidates from './components/JobCandidates';
import React from 'react';
import ResetPassword from './components/auth/ResetPassword';
import Dashboard from './components/Dashboard';
import ScrollToTop from './components/ScrollToTop';
import VideoInterviewLanding from './components/VideoInterviewLanding';
import VideoInterview from './components/video-interview/VideoInterview';
import { RootStoreProvider } from './stores/rootStore';
import InterviewMagicLink from './components/InterviewMagicLink';
import VideoPlayer from './components/VideoPlayer';
import Team from './components/Team';
import TeamInvitation from './components/TeamInvitation';
import {
  PricingPage,
  SubscriptionSuccess,
  SubscriptionCancel,
  BillingSettings,
  CreditsSuccess,
  CreditsCancel,
  PaymentFailureBanner
} from './components/subscription';
import ATSDecision from './components/auth/ATSDecision';
import ATSSelector from './components/auth/ATSSelector';
import ATSConnectPage from './components/integrations/ATSConnectPage';
import ATSImportJob from './components/integrations/ATSImportJob';
import ATSInviteCandidates from './components/integrations/ATSInviteCandidates';
import createApolloCache from './apolloCache';

const Users = React.lazy(() => import('./components/admin/users/Users'));
const Clients = React.lazy(() => import('./components/admin/clients/Clients'));
const InterviewSections = React.lazy(() => import('./components/admin/interview-sections/InterviewSections'));
const Positions = React.lazy(() => import('./components/admin/positions/Positions'));
const EvaluationPrompts = React.lazy(() => import('./components/admin/evaluation-prompts/EvaluationPrompts'));
const Tiers = React.lazy(() => import('./components/admin/tiers/Tiers'));
const AdminDashboard = React.lazy(() => import('./components/admin/AdminDashboard'));

function App() {

  console.log("Cavalry app, version", config.version);

  if (config.sentryDsn) {
    Sentry.init({
      dsn: config.sentryDsn,
      release: "cavalry@" + config.version,
      integrations: [
        new CaptureConsole({
          levels: ['error'],
        }),
      ],
    });
  }

  const httpLink = createUploadLink({
    uri: config.apiUrl + "/graphql",
    fetch: customFetch,
    // fetchOptions: {
    //   onProgress: (progress) => {
    //     console.log(progress);
    //   },
    // },
  });

  const authLink = setContext((_, { headers }) => {
    // console.warn("authLink", localStorage.getItem('AUTH_TOKEN_NAME'), _);
    const token = localStorage.getItem(AUTH_TOKEN_NAME);
    return {
      headers: {
        ...headers,
        authorization: token ? `Bearer ${token}` : "",
      }
    }
  });

  const client = new ApolloClient({
    // uri: config.apiUrl + "/graphql",
    link: authLink.concat(httpLink),
    cache: createApolloCache(),
    // headers: {
    //   authorization: "Token " + localStorage.getItem("AUTH_TOKEN_NAME"),
    // }
  });

  const theme = extendTheme({
    colorSchemes: {
      light: {
        palette: {
          primary: {
            400: "#3b3bcd",
            500: "#3b3bcd",
          }
        },
      }
    }
  });

  // GraphQL mutation used by the new MobX uploader store
  const UPLOAD_CHUNK = gql`
    mutation UploadChunk($_id: String!, $blob: Upload!) {
      chunkUpload(_id: $_id, blob: $blob)
    }
  `;

  // Adapter to pass into RootStoreProvider -> UploaderStore
  const uploadFn = async (_id, blob, onProgress) => {
    await client.mutate({
      mutation: UPLOAD_CHUNK,
      variables: { _id, blob },
      context: {
        fetchOptions: {
          useUpload: true,
          onProgress,
          onAbortPossible: () => { },
        },
      },
    });
  };

  return (
    <ErrorBoundary>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <GoogleOAuthProvider clientId={config.googleClientId}>
          <ApolloProvider client={client}>
            <BrowserRouter>
              <FeedbackProvider>
                <CssVarsProvider theme={theme}>
                  <CssBaseline />
                  <PaymentFailureBanner />
                  <ScrollToTop>
                    <Routes>
                      <Route index element={<Home />} />
                      <Route path="/interview" element={<InterviewMagicLink />} />
                      <Route path="/invitation/:token" element={<TeamInvitation />} />
                      <Route path="/video/:interviewId" element={<VideoPlayer />} />
                      <Route path="/company/:clientId/job/:jobId">
                        <Route element={<AuthLayout isApply />}>
                          <Route path="intro" element={<SalesLanding />} />
                          <Route path="sign-in" element={<SignInForm />} />
                          <Route path="sign-up" element={<SignUpForm />} />
                          <Route path="confirm-email" element={<ConfirmEmail />} />
                          <Route path="apply-profile" element={<Talent />} />
                        </Route>
                        <Route element={<MainLayout />}>
                          <Route path='questionnaire' element={<Questionnaire />} />
                          <Route path='video-interview-intro' element={<VideoInterviewLanding />} />
                          <Route path='video-interview-old' element={<VideoInterview />} />
                          <Route path='video-interview' element={
                            <RootStoreProvider apolloClient={client} uploadFn={uploadFn}>
                              <VideoInterview />
                            </RootStoreProvider>
                          } />
                        </Route>
                      </Route>
                      <Route element={<AuthLayout />}>
                        <Route path="sign-in" element={<SignInForm />} />
                        <Route path="sign-up/:inviteCode?" element={<SignUpForm />} />
                        <Route path='reset-password' element={<ResetPassword />} />
                        <Route path="confirm-email" element={<ConfirmEmail />} />
                        <Route path="start-company" element={<Company />} />
                        <Route path="select-plan" element={<PricingPage />} />
                        <Route path="onboarding/ats-decision" element={<ATSDecision />} />
                        <Route path="onboarding/ats-selector" element={<ATSSelector />} />
                      </Route>
                      <Route path="subscription">
                        <Route path="success" element={<SubscriptionSuccess />} />
                        <Route path="cancel" element={<SubscriptionCancel />} />
                      </Route>
                      <Route path="credits">
                        <Route path="success" element={<CreditsSuccess />} />
                        <Route path="cancel" element={<CreditsCancel />} />
                      </Route>
                      <Route element={<MainLayout />}>
                        <Route path="dashboard" element={<Dashboard />} />
                        <Route path="dashboard/:applicationId" element={<Dashboard />} />
                        <Route path="account" element={<Account />} />
                        <Route path="company" element={<Company />} />
                        <Route path="team" element={<Team />} />
                        <Route path="settings">
                          <Route path="billing">
                            <Route index element={<BillingSettings />} />
                            <Route path="plan" element={<PricingPage />} />
                          </Route>
                        </Route>
                        <Route path="onboarding/connect-ats" element={<ATSConnectPage />} />
                        <Route path="onboarding/import-job" element={<ATSImportJob />} />
                        <Route path="onboarding/job/:jobId" element={<OnboardingJobEditor />} />
                        <Route path="onboarding/invite-candidates/:jobId" element={<ATSInviteCandidates />} />
                        <Route path="jobs" element={<Jobs />} />
                        <Route path="job/new" element={<Navigate to="/onboarding/job/new" replace />} />
                        <Route path="job/:jobId" element={<Job />} />
                        <Route path="job/:jobId/candidates" element={<JobCandidates />} />
                        <Route path="job/:jobId/candidates/:applicationId" element={<JobCandidates />} />
                        <Route path="profile" element={<Talent />} />
                        <Route path="applications" element={<Applications />} />
                        <Route path="admin">
                          <Route path="dashboard" element={<AdminDashboard />} />
                          <Route path="users" element={<Users />} />
                          <Route path="companies" element={<Clients />} />
                          <Route path="companies/:clientId/:jobId" element={<Clients />} />
                          <Route path="companies/:clientId/:jobId/:applicationId" element={<Clients />} />
                          <Route path="interview-sections" element={<InterviewSections />} />
                          <Route path="positions" element={<Positions />} />
                          <Route path="evaluation-prompts" element={<EvaluationPrompts />} />
                          <Route path="tiers" element={<Tiers />} />
                        </Route>
                      </Route>
                    </Routes>
                  </ScrollToTop>
                </CssVarsProvider>
              </FeedbackProvider>
            </BrowserRouter>
          </ApolloProvider>
        </GoogleOAuthProvider>
      </LocalizationProvider>
    </ErrorBoundary>
  )
}

export default App;
